import { ChevronDown, Filter, Plus, RefreshCw } from 'lucide-react';
import React, { ReactNode } from 'react';
import { SearchableDropdown } from '../forms';

export interface ModuleLayoutProps {
  // Header Section
  title: string;
  description: string;
  icon: ReactNode;
  primaryAction?: {
    label: string;
    onClick: () => void;
    icon?: ReactNode;
  };

  // View Controls
  viewMode?: 'grid' | 'list' | 'calendar' | 'map';
  onViewModeChange?: (mode: string) => void;
  viewOptions?: Array<{
    value: string;
    label: string;
    icon: ReactNode;
  }>;

  // Quick Stats (now displayed inline in header)
  stats?: Array<{
    title: string;
    value: number | string;
    icon?: ReactNode;
    variant?: 'brand' | 'success' | 'warning' | 'danger' | 'info' | 'purple';
  }>;

  // Filters
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  filters?: ReactNode;
  showAdvancedFilters?: boolean;
  onToggleAdvancedFilters?: () => void;

  // Sorting & Actions
  sortOptions?: Array<{
    value: string;
    label: string;
    description?: string;
  }>;
  sortValue?: string;
  onSortChange?: (value: string) => void;
  sortOrder?: 'asc' | 'desc';
  onSortOrderChange?: (order: 'asc' | 'desc') => void;

  // Additional Actions
  onRefresh?: () => void;
  isLoading?: boolean;
  additionalActions?: ReactNode;

  // Content
  children: ReactNode;

  // Pagination
  pagination?: ReactNode;
}

// Helper to get stat color based on variant
const getStatColor = (variant?: string) => {
  switch (variant) {
    case 'success':
      return 'text-green-600';
    case 'warning':
      return 'text-yellow-600';
    case 'danger':
      return 'text-red-600';
    case 'info':
      return 'text-blue-600';
    case 'purple':
      return 'text-purple-600';
    default:
      return 'text-brand-600';
  }
};

const ModuleLayout: React.FC<ModuleLayoutProps> = ({
  title,
  description,
  icon,
  primaryAction,
  viewMode = 'list',
  onViewModeChange,
  viewOptions,
  stats,
  searchValue,
  onSearchChange,
  filters,
  showAdvancedFilters = false,
  onToggleAdvancedFilters,
  sortOptions,
  sortValue,
  onSortChange,
  sortOrder = 'desc',
  onSortOrderChange,
  onRefresh,
  isLoading = false,
  additionalActions,
  children,
  pagination,
}) => {
  return (
    <div className="absolute inset-0 flex flex-col">
      {/* Compact Header Bar */}
      <div className="flex-none bg-white border-b border-gray-200">
        <div className="px-5 py-2.5">
          <div className="flex items-center justify-between">
            {/* Left: Title + Inline Stats */}
            <div className="flex items-center space-x-5">
              <div className="flex items-center space-x-2.5">
                {icon && (
                  <div className="w-8 h-8 bg-brand-50 rounded-lg flex items-center justify-center flex-none">
                    {icon}
                  </div>
                )}
                <div>
                  <h1 className="text-base font-semibold text-gray-900 leading-tight">{title}</h1>
                  <p className="text-[11px] text-gray-500 leading-tight">{description}</p>
                </div>
              </div>

              {/* Inline Stats - Compact */}
              {stats && stats.length > 0 && (
                <div className="hidden lg:flex items-center space-x-3 pl-5 border-l border-gray-200">
                  {stats.slice(0, 4).map((stat, idx) => (
                    <div key={idx} className="flex items-center space-x-1">
                      <span className={`text-xs font-semibold ${getStatColor(stat.variant)}`}>
                        {stat.value}
                      </span>
                      <span className="text-[10px] text-gray-400">{stat.title}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right: Actions */}
            <div className="flex items-center space-x-2">
              {/* View Mode Toggle - Compact */}
              {viewOptions && onViewModeChange && (
                <div className="flex items-center bg-gray-100 rounded-md p-0.5">
                  {viewOptions.map(option => (
                    <button
                      key={option.value}
                      onClick={() => onViewModeChange(option.value)}
                      className={`p-1.5 rounded transition-colors ${
                        viewMode === option.value
                          ? 'bg-white text-brand-600 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                      title={option.label}
                    >
                      {option.icon}
                    </button>
                  ))}
                </div>
              )}

              {/* Refresh Button */}
              {onRefresh && (
                <button
                  onClick={onRefresh}
                  disabled={isLoading}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
                  title="Refresh"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
                </button>
              )}

              {/* Additional Actions */}
              {additionalActions}

              {/* Primary Action */}
              {primaryAction && (
                <button
                  onClick={primaryAction.onClick}
                  className="inline-flex items-center px-3 py-1.5 text-[11px] font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-md transition-colors"
                >
                  {primaryAction.icon || <Plus className="h-3 w-3 mr-1" />}
                  {primaryAction.label}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar Row - Filters & Sort (if any filters/sort provided) */}
      {(onSearchChange || filters || sortOptions) && (
        <div className="flex-none bg-white border-b border-gray-100 px-5 py-2">
          <div className="flex items-center justify-between gap-4">
            {/* Left: Search & Filters */}
            <div className="flex items-center gap-3">
              {/* Search */}
              {onSearchChange && (
                <div className="relative w-40">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchValue || ''}
                    onChange={e => onSearchChange(e.target.value)}
                    className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                  />
                  <div className="absolute left-2.5 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <svg
                      className="h-3.5 w-3.5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                </div>
              )}

              {/* Custom Filters */}
              {filters && <div className="flex-shrink-0">{filters}</div>}

              {/* Advanced Filters Toggle */}
              {onToggleAdvancedFilters && (
                <button
                  onClick={onToggleAdvancedFilters}
                  className="flex items-center text-[11px] text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <Filter className="h-3 w-3 mr-1" />
                  Filters
                  <ChevronDown
                    className={`h-3 w-3 ml-0.5 transform transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`}
                  />
                </button>
              )}
            </div>

            {/* Right: Sort */}
            {sortOptions && onSortChange && (
              <div className="flex items-center gap-2">
                <div className="w-48">
                  <SearchableDropdown
                    placeholder="Sort by..."
                    value={sortValue || 'createdAt'}
                    onChange={onSortChange}
                    options={sortOptions}
                  />
                </div>

                {onSortOrderChange && (
                  <div className="w-24">
                    <SearchableDropdown
                      placeholder="Order"
                      value={sortOrder}
                      onChange={value => onSortOrderChange(value as 'asc' | 'desc')}
                      options={[
                        { value: 'asc', label: 'Asc' },
                        { value: 'desc', label: 'Desc' },
                      ]}
                      searchable={false}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 min-h-0 p-4 bg-gray-50">
        <div className="h-full bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col">
          {/* Content */}
          <div className="flex-1 min-h-0 overflow-auto p-4">{children}</div>

          {/* Pagination - Inside card */}
          {pagination && (
            <div className="flex-none border-t border-gray-100 px-4 py-2 bg-gray-50">
              {pagination}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModuleLayout;
